/**
 * The split's pure geometry and persistence, kept out of the component so it can be unit-tested
 * without a DOM. (Apocryph.ai does the same thing with its zoom/pan maths, and it is the reason
 * those are the only component internals with real test coverage.)
 */

export type SplitAxis = "stacked" | "side-by-side";

/** Never let either region collapse to nothing — a dock you cannot grab back is a trap. */
export const MIN_REGION_PX = 120;

/**
 * Wide-window behaviour is remembered PER WINDOW-WIDTH BUCKET, not globally: docking to the
 * ultrawide's right third and moving to the main monitor must each restore their own choice.
 * 1600 is comfortably above the 1147px primary window and below a maximised 2400px one.
 */
export const WIDE_WINDOW_PX = 1600;

export function widthBucket(windowWidth: number): "narrow" | "wide" {
  return windowWidth >= WIDE_WINDOW_PX ? "wide" : "narrow";
}

/** Clamp a requested dock size so both regions keep at least MIN_REGION_PX. */
export function clampDockSize(requested: number, total: number): number {
  const max = total - MIN_REGION_PX;
  if (max <= MIN_REGION_PX) return Math.max(0, Math.floor(total / 2));
  return Math.min(Math.max(requested, MIN_REGION_PX), max);
}

/**
 * Dock size from a pointer position. The dock is the LOWER (or right) region, so its size is the
 * distance from the pointer to the far edge.
 */
export function dockSizeFromPointer(
  pointer: number,
  containerStart: number,
  containerSize: number,
): number {
  return clampDockSize(containerSize - (pointer - containerStart), containerSize);
}

const RATIO_KEY = (surface: string, axis: SplitAxis) => `nihongonator:split:${surface}:${axis}`;
const AXIS_KEY = (surface: string, bucket: string) => `nihongonator:axis:${surface}:${bucket}`;

/** Per device, never synced — the design is explicit that split state is local. */
export function loadDockSize(surface: string, axis: SplitAxis, fallback: number): number {
  try {
    const raw = localStorage.getItem(RATIO_KEY(surface, axis));
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

export function saveDockSize(surface: string, axis: SplitAxis, size: number): void {
  try {
    localStorage.setItem(RATIO_KEY(surface, axis), String(Math.round(size)));
  } catch {
    /* private browsing, quota — the split still works, it just will not persist */
  }
}

export function loadAxis(surface: string, windowWidth: number): SplitAxis {
  const bucket = widthBucket(windowWidth);
  try {
    const raw = localStorage.getItem(AXIS_KEY(surface, bucket));
    if (raw === "stacked" || raw === "side-by-side") return raw;
  } catch {
    /* fall through to the default */
  }
  // Stacked is the only sensible default in the primary window; wide windows get the rotated split.
  return bucket === "wide" ? "side-by-side" : "stacked";
}

export function saveAxis(surface: string, windowWidth: number, axis: SplitAxis): void {
  try {
    localStorage.setItem(AXIS_KEY(surface, widthBucket(windowWidth)), axis);
  } catch {
    /* as above */
  }
}

/**
 * On a phone the vertical split becomes a bottom sheet — "the same grip, the same three positions".
 * `peek` is the design's ~40% resting height; `full` is dragged up; `hidden` is swiped away to
 * leave only the text.
 */
export type SheetPosition = "hidden" | "peek" | "full";

export const SHEET_POSITIONS: readonly SheetPosition[] = ["hidden", "peek", "full"];

/** Fractions of the available height. `hidden` still shows the grip, or it could never come back. */
export const SHEET_HEIGHT: Record<SheetPosition, number> = {
  hidden: 0,
  peek: 0.4,
  full: 0.92,
};

/** The position whose height is closest to a dragged one — what the sheet snaps to on release. */
export function snapSheet(heightFraction: number): SheetPosition {
  let best: SheetPosition = "peek";
  let distance = Infinity;
  for (const position of SHEET_POSITIONS) {
    const gap = Math.abs(SHEET_HEIGHT[position] - heightFraction);
    if (gap < distance) {
      distance = gap;
      best = position;
    }
  }
  return best;
}

/** Tapping the grip cycles rather than dragging — the shortest path to "show me more". */
export function cycleSheet(position: SheetPosition): SheetPosition {
  return position === "peek" ? "full" : position === "full" ? "hidden" : "peek";
}

const SHEET_KEY = (surface: string) => `nihongonator:sheet:${surface}`;

export function loadSheetPosition(surface: string, fallback: SheetPosition = "peek"): SheetPosition {
  try {
    const raw = localStorage.getItem(SHEET_KEY(surface));
    if (raw === "hidden" || raw === "peek" || raw === "full") return raw;
  } catch {
    /* fall through */
  }
  return fallback;
}

export function saveSheetPosition(surface: string, position: SheetPosition): void {
  try {
    localStorage.setItem(SHEET_KEY(surface), position);
  } catch {
    /* as above */
  }
}
